import React, { useState, useEffect, useMemo } from 'react';
import { IntensityRowSection } from './IntensityGraphCard';
import IntensitySummaryCard from './IntensitySummaryCard';
import { generatePeriods, filterPeriods, fyDateRange, cfgLabel, useIntensityData, useIntensityFilters, IntensityFilterBar, S } from '../../../../Component/Intensity/intensityShared';

function getStartingMonth() {
  return parseInt(JSON.parse(localStorage.getItem('currentUser') || '{}')?.starting_month || '4', 10);
}

export const Intensity = ({ financialYearId: dashboardFYId, financialYear: fyList = [] }) => {
  const startingMonth = getStartingMonth();

  const latestFY = useMemo(
    () => [...fyList].sort((a, b) => parseInt(b.financial_year_value) - parseInt(a.financial_year_value))[0],
    [fyList],
  );

  const [selectedFYIds,    setSelectedFYIds]    = useState([]);
  const [activeTab,        setActiveTab]        = useState('graphs');
  const [xAxis,            setXAxis]            = useState('period');
  const [yAxis,            setYAxis]            = useState('none');
  const [sortBy,           setSortBy]           = useState('value-desc');
  const [limit,            setLimit]            = useState(null);
  const [userSetLimit,     setUserSetLimit]     = useState(false);
  const [chartOverride,    setChartOverride]    = useState('line');
  const [insightsEnabled,  setInsightsEnabled]  = useState(false);

  const filters = useIntensityFilters();
  const { setFromDate, setToDate, aggregation, effectiveDates, selectedWeekMonths, selectedQuarters, selectedHalves } = filters;

  const {
    locations, selectedLocIds, setSelectedLocIds,
    allConfigs, selectedConfigIds, setSelectedConfigIds,
    kpiOptions, selectedKpis, setSelectedKpis,
    denomData, filteredDenomData, selectedDenomIds, setSelectedDenomIds,
    loading, tableGroups,
  } = useIntensityData({
    fromDate: effectiveDates.fromDate,
    toDate:   effectiveDates.toDate,
    aggregation,
    groupByLocation: xAxis === 'location' || yAxis === 'location',
  });

  // Default to the dashboard's active FY on load
  useEffect(() => {
    const fy = fyList.find(f => String(f.id) === String(dashboardFYId)) || latestFY;
    if (!fy) return;
    setSelectedFYIds([String(fy.id)]);
  }, [dashboardFYId, latestFY]); // eslint-disable-line

  // Span from/to across all selected FYs
  useEffect(() => {
    if (!selectedFYIds.length) return;
    const selected = fyList.filter(f => selectedFYIds.includes(String(f.id)));
    if (!selected.length) return;
    const ranges = selected.map(fy => fyDateRange(fy, startingMonth));
    setFromDate([...ranges.map(r => r.fromDate)].sort()[0]);
    setToDate([...ranges.map(r => r.toDate)].sort().reverse()[0]);
  }, [selectedFYIds.join(',')]); // eslint-disable-line

  const handleXAxisChange = val => { setXAxis(val); if (yAxis === val) setYAxis('none'); setUserSetLimit(false); };
  const handleYAxisChange = val => { setYAxis(val); setUserSetLimit(false); };
  const handleLimitChange = val => { setUserSetLimit(val !== null && val > 0); setLimit(val); };

  const allPeriods = useMemo(
    () => generatePeriods(effectiveDates.fromDate, effectiveDates.toDate, aggregation),
    [effectiveDates.fromDate, effectiveDates.toDate, aggregation], // eslint-disable-line
  );
  const filteredPeriods = useMemo(
    () => filterPeriods(allPeriods, aggregation, selectedWeekMonths, selectedQuarters, selectedHalves),
    [allPeriods, aggregation, selectedWeekMonths, selectedQuarters, selectedHalves], // eslint-disable-line
  );
  const totalRows = tableGroups.reduce((acc, g) => acc + g.topRows.length + g.kpiRows.length, 0);

  // Max number of entities the limit applies to. Y=none → limit on X-axis items; Y≠none → limit on Y-axis series.
  const limitMax = useMemo(() => {
    if (yAxis === 'none') {
      if (xAxis === 'period') return filteredPeriods.length;
      if (xAxis === 'location') return selectedLocIds.length;
      if (xAxis === 'financial-year') return selectedFYIds.length;
    }
    if (yAxis === 'location') return selectedLocIds.length;
    if (yAxis === 'financial-year') return selectedFYIds.length;
    return null;
  }, [yAxis, xAxis, filteredPeriods.length, selectedLocIds.length, selectedFYIds.length]); // eslint-disable-line

  // Auto smart default: min(10, limitMax). Runs whenever limitMax changes (including
  // transient values during data load) so limit always reflects the latest available count.
  // Skipped when user has manually typed a value that's still within bounds.
  useEffect(() => {
    if (limitMax != null && limitMax > 0)
      setLimit(prev => (!userSetLimit || prev === null || prev <= 0 || prev > limitMax) ? Math.min(10, limitMax) : prev);
  }, [limitMax, userSetLimit]); // eslint-disable-line

  return (
    <div style={{ padding: '20px 24px', fontFamily: "'Inter', -apple-system, sans-serif", fontSize: 14, color: '#1f2937', background: '#f8fafc', minHeight: '100%' }}>

      <IntensityFilterBar
        filters={filters}
        data={{ locations, selectedLocIds, setSelectedLocIds, allConfigs, selectedConfigIds, setSelectedConfigIds, kpiOptions, selectedKpis, setSelectedKpis, denomData, selectedDenomIds, setSelectedDenomIds }}
        totalRows={totalRows}
        periodCount={filteredPeriods.length}
        allPeriods={allPeriods}
        fySelector={{ fyList, selectedFYIds, onFYChange: setSelectedFYIds, startingMonth }}
        tabToggle={
          <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: 8, padding: 3, gap: 2 }}>
            {[{ id: 'graphs', label: '📊 Graphs' }, { id: 'summary', label: '🃏 Summary' }].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '6px 16px', borderRadius: 6, border: 'none', fontSize: 13,
                  fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s',
                  ...(activeTab === tab.id
                    ? { background: 'linear-gradient(135deg, #3f88a7 0%, #2f6e8a 100%)', color: '#fff', boxShadow: '0 1px 4px rgba(63,136,167,0.35)' }
                    : { background: 'transparent', color: '#6b7280' }
                  ),
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        }
        graphAxes={activeTab === 'graphs' ? { xAxis, yAxis, onXAxisChange: handleXAxisChange, onYAxisChange: handleYAxisChange, sortBy, onSortByChange: setSortBy, limit, onLimitChange: handleLimitChange, limitMax, chartOverride, onChartTypeChange: setChartOverride } : null}
      />

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
          <div style={S.spinner} />
        </div>
      ) : selectedLocIds.length === 0 ? (
        <div style={emptyState}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>📍</div>
          <div style={{ fontWeight: 600, color: '#374151' }}>No location selected</div>
        </div>
      ) : allConfigs.length === 0 ? (
        <div style={emptyState}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>⚙️</div>
          <div style={{ fontWeight: 600, color: '#374151' }}>No intensity types configured</div>
          <div style={{ color: '#6b7280', fontSize: 13, marginTop: 4 }}>Go to Settings → Intensity to set up your types.</div>
        </div>
      ) : tableGroups.length === 0 ? (
        <div style={emptyState}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>📊</div>
          <div style={{ fontWeight: 600, color: '#374151' }}>No data for selected filters</div>
        </div>
      ) : activeTab === 'graphs' ? (
        <>
          {/* ── Insights toggle ── */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
            <button
              onClick={() => setInsightsEnabled(v => !v)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '4px 11px',
                borderRadius: 7,
                border: insightsEnabled ? '1px solid #7c3aed30' : '1px solid #e2e8f0',
                background: insightsEnabled ? '#faf5ff' : 'transparent',
                color: insightsEnabled ? '#7c3aed' : '#94a3b8',
                fontSize: 11, fontWeight: 600,
                fontFamily: "'DM Mono', monospace", letterSpacing: '0.06em',
                cursor: 'pointer', transition: 'all 0.15s', outline: 'none',
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
                strokeLinecap="round" strokeLinejoin="round"
                style={{ width: 12, height: 12, minWidth: 12, display: 'block' }}>
                <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z"/>
              </svg>
              Insights
              <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 28, height: 15, borderRadius: 99,
                background: insightsEnabled ? '#7c3aed' : '#cbd5e1',
                transition: 'background 0.2s', position: 'relative', flexShrink: 0,
              }}>
                <span style={{
                  width: 11, height: 11, borderRadius: '50%', background: 'white',
                  position: 'absolute',
                  left: insightsEnabled ? 'calc(100% - 13px)' : 2,
                  transition: 'left 0.2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }} />
              </span>
            </button>
          </div>

          {tableGroups.map(({ configId, topRows, kpiRows }) => (
            <div key={configId} style={{ marginBottom: 8 }}>
              {topRows.map(row => (
                <IntensityRowSection
                  key={`${configId}-top-${row.label}`}
                  row={row} denomData={filteredDenomData}
                  allPeriods={filteredPeriods} locations={locations}
                  xAxis={xAxis} yAxis={yAxis} sortBy={sortBy} limit={limit} startingMonth={startingMonth}
                  chartOverride={chartOverride} insightsEnabled={insightsEnabled}
                />
              ))}
              {kpiRows.map(row => (
                <div key={`${configId}-kpi-${row.label}`} style={{ paddingLeft: 24 }}>
                  <IntensityRowSection
                    row={row} denomData={filteredDenomData}
                    allPeriods={filteredPeriods} locations={locations}
                    xAxis={xAxis} yAxis={yAxis} sortBy={sortBy} limit={limit}
                    chartOverride={chartOverride} insightsEnabled={insightsEnabled}
                  />
                </div>
              ))}
            </div>
          ))}
        </>
      ) : (
        <IntensitySummaryCard
          tableGroups={tableGroups}
          filteredDenomData={filteredDenomData}
          filteredPeriods={filteredPeriods}
          cfgLabel={cfgLabel}
        />
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

const emptyState = {
  display: 'flex', flexDirection: 'column',
  alignItems: 'center', justifyContent: 'center',
  padding: 60, gap: 4, textAlign: 'center',
};
